import { useState } from "react";

import Grid from "@Components/Grid";
import Paper from "@Components/Paper";
import Button from "@Components/Button";
import { formatDate } from "@Utils/functions";
import { getDmvStatus, TitleIssue } from "@Types/title_issues";
import { getNextCheckDate } from "@Pages/Applications/Applications";
import { renderFieldData } from "@Components/FieldData/RenderFieldData";

import DMVDialog from "./DmvChecker";
import WisconsinLienCheckDialog from "./WisconsinLienCheckDialog";
import styles from "./DmvInfo.module.scss";
import { CollateralTypes } from "@Types/deal";

export const SupportedStates = [
  "South Dakota",
  "North Dakota",
  "Minnesota",
  "Illinois",
  "Wisconsin",
];

const isDMVCheckSupported = (state: string, type: CollateralTypes) => {
  if (SupportedStates.includes(state) && type === "Automotive") {
    return true;
  }
  return false;
};

const DmvInfo = ({ titleIssue }: { titleIssue: TitleIssue }) => {
  const [open, setOpen] = useState(false);
  const [wiLienOpen, setWiLienOpen] = useState(false);

  const openDialog = () => setOpen(true);
  const info = titleIssue?.data?.info;

  const status = getDmvStatus(info?.dmvInfo?.status);
  const lienInfo = info?.lienInfo;

  const deal = titleIssue.data.deal;
  const currentState = deal?.data.applicant.data.info.currentState;
  const isWisconsin = currentState === "Wisconsin";

  return (
    <Paper title="Dmv Status">
      <Grid container xs={12}>
        <Grid xs={12}>
          {renderFieldData("Status", status, {
            ...statusStyles.base,
            ...(status === "Titled"
              ? statusStyles.statusTitled
              : status === "Untitled"
              ? statusStyles.statusUntitled
              : statusStyles.statusNotChecked),
          })}
        </Grid>

        <Grid xs={12}>
          {renderFieldData(
            "Last checked date",
            info?.lastCheckedDate ? formatDate(info?.lastCheckedDate) : "N/A"
          )}
        </Grid>
        {status !== "Titled" && (
          <Grid xs={12}>
            {renderFieldData(
              "Next check date",
              titleIssue ? getNextCheckDate(titleIssue) : "N/A"
            )}
          </Grid>
        )}
        {info?.dmvInfo?.titleImageUrl && (
          <Grid container xs={12}>
            <Grid xs={6} className={styles.label}>
              DMV Check Image
            </Grid>
            <Grid xs={6} className={styles.data}>
              <a
                href={info?.dmvInfo.titleImageUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                URL
              </a>
            </Grid>
          </Grid>
        )}

        {/* ── Wisconsin Lienholder Info ── */}
        {lienInfo && (
          <>
            <Grid xs={12}>
              <div style={{ margin: "10px 0 4px", fontWeight: 600, fontSize: 15 }}>
                WI Lien Check
              </div>
            </Grid>
            <Grid xs={12}>
              {renderFieldData(
                "Lien Present",
                lienInfo.hasLien ? "YES" : "NO",
                { fontWeight: 700, color: lienInfo.hasLien ? "#d32f2f" : "rgb(80,165,56)" }
              )}
            </Grid>
            {lienInfo.hasLien && (
              <>
                <Grid xs={12}>
                  {renderFieldData("Lienholder Name", lienInfo.lienholderName ?? "N/A")}
                </Grid>
                <Grid xs={12}>
                  {renderFieldData("Lienholder Address", lienInfo.lienholderAddress ?? "N/A")}
                </Grid>
                {lienInfo.receivedDate && (
                  <Grid xs={12}>
                    {renderFieldData("Received Date", lienInfo.receivedDate)}
                  </Grid>
                )}
              </>
            )}
            <Grid xs={12}>
              {renderFieldData(
                "Last checked",
                new Date(lienInfo.checkedAt).toLocaleString()
              )}
            </Grid>
          </>
        )}
      </Grid>

      {deal && currentState && isDMVCheckSupported(currentState, deal.data.info.type) ? (
        <Grid container xs={12} spacing={1}>
          {!isWisconsin && (
            <Grid xs={6}>
              <Button variant="blue" onClick={openDialog}>
                Perform DMV Check
              </Button>
            </Grid>
          )}
          {isWisconsin && (
            <Grid xs={6}>
              <Button variant="blue" onClick={() => setWiLienOpen(true)}>
                WI Lien Check
              </Button>
            </Grid>
          )}
          <DMVDialog
            deal={deal}
            open={open}
            setOpen={setOpen}
            titleIssue={titleIssue}
            currentState={currentState || null}
          />
          <WisconsinLienCheckDialog
            titleIssue={titleIssue}
            open={wiLienOpen}
            setOpen={setWiLienOpen}
          />
        </Grid>
      ) : null}
    </Paper>
  );
};

export default DmvInfo;

const statusStyles = {
  base: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 18px",
    borderRadius: "16px",
    fontSize: "18px",
    fontWeight: 600,
    textTransform: "capitalize",
    whiteSpace: "nowrap",
  },
  statusUntitled: {
    backgroundColor: "#ffbb6c",
    color: "#fff",
  },
  statusTitled: {
    backgroundColor: "#10b981",
    color: "#fff",
  },
  statusNotChecked: {
    backgroundColor: "#f1f5f9",
    color: "#475569",
  },
};
